<template>
  <main id="main">
    <Loading :isProcessing="isProcessing"></Loading>
    <DiagCorpHeader
      ref="corpHeader"
      v-if="!isRendering && isAdminUser && !$route.meta.isAccountEdit"
      :corpName="dispCorpNm"
      :corporateFeatureInfoLabels="corporateFeatureInfoLabels"
      :corporateSelectable="isAdmin || isSystemAdmin || isAllPermission"
      :manageCorpName="manageCorpName"
      :isAdmin="isAdmin"
      :isDiagAvailablePlan="isDiagAvailablePlan"
      @select-corporate="emitSelectCorpList"
    ></DiagCorpHeader>
    <div class="l-contents" v-if="!isRendering">
      <h1 class="l-pageTitle"><span class="l-pageLabel">{{ $route.meta.isAccountEdit ? 'アカウント情報' : '法人情報' }}</span>事業場</h1>
      <CorporateSideMenu :isNew="false"/>
      <div class="l-contentsBox">

        <p class="l-text u-mb20" style="font-weight: bold;">事業場を登録して、各事業場における届出項目の要否と対応状況を管理できます。</p>

        <!-- 凡例 + 操作ボタン -->
        <div class="ws-guide">
          <div class="ws-guide__cards">
            <div class="ws-guide__card ws-guide__card--green">
              <p class="ws-guide__title is-green"><span class="ws-guide__dot ws-guide__dot--green"></span>緑色の項目：自動判定</p>
              <p class="ws-guide__body">従業員数を選択すると、対応の要否が自動的に判定されます。必要と判定された項目について対応しているかチェックしてください。</p>
            </div>
            <div class="ws-guide__card ws-guide__card--blue">
              <p class="ws-guide__title is-blue"><span class="ws-guide__dot ws-guide__dot--blue"></span>青色の項目：手動判定</p>
              <p class="ws-guide__body">業種の状況に応じて適用されるかどうかが異なります。ご自身で要否を選択した上で、対応状況を管理してください。</p>
            </div>
          </div>
          <div class="ws-guide__btns">
            <button type="button" class="ws-btn-save" @click="save()">全データを保存</button>
            <button type="button" class="ws-btn-add" :disabled="!canAddWorkplace" @click="addWorkplace()">事業場を追加</button>
          </div>
        </div>

        <div v-if="workplaces.length === 0" class="l-contentsBox l-resultBox">
          <h3 class="l-headding is-blue">事業場の情報が登録されていません。</h3>
        </div>

        <!-- テーブル(列=事業場 / 行=届出項目) -->
        <div v-else class="ws-scroll-wrap" ref="scrollWrap">
          <div class="ws-tbl">

            <!-- ヘッダー行 -->
            <div class="ws-row ws-row--head">
              <div class="ws-col ws-col--label">届出項目 / 事業場リスト</div>
              <div class="ws-col ws-col--place is-header" v-for="(wp, idx) in workplaces" :key="idx">
                <div class="ws-place-name">
                  <span v-if="!wp._editing">{{ wp.workplaceName }}</span>
                  <input
                    v-else
                    type="text"
                    class="ws-place-name-input"
                    placeholder="事業場名"
                    v-model.trim="wp.workplaceName"
                    @blur="wp._editing = false"
                    @keyup.enter="wp._editing = false"
                  />
                  <button type="button" class="ws-place-edit" title="編集" @click="startEditName(wp, $event)">
                    <img src="@/assets/img/common/icon-edit.svg" alt="編集">
                  </button>
                  <button type="button" class="ws-place-del" title="削除" @click="removeWorkplace(idx)"></button>
                </div>
                <select class="ws-select" v-model.number="wp.employeesType" @change="onEmployeesTypeChange(wp)">
                  <option :value="null">従業員数を選択</option>
                  <option v-for="opt in empTypeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
              </div>
            </div>

            <!-- 備考行 -->
            <div class="ws-row ws-row--note">
              <div class="ws-col ws-col--label"><span class="ws-note-icon"></span>備考</div>
              <div class="ws-col ws-col--place" v-for="(wp, idx) in workplaces" :key="idx">
                <textarea class="ws-note-input" placeholder="メモ・備考（100字）" maxlength="100" v-model="wp.remarks"></textarea>
              </div>
            </div>

            <!-- 届出項目行 -->
            <div
              v-for="item in notificationItems"
              :key="item.workplaceNotificationItemId"
              class="ws-row ws-row--item"
              :class="item.judgmentType === JUDGMENT_TYPE_AUTO ? 'ws-row--auto' : 'ws-row--manual'"
            >
              <div class="ws-col ws-col--label">
                <span class="ws-tag" :class="item.judgmentType === JUDGMENT_TYPE_AUTO ? 'ws-tag--auto' : 'ws-tag--manual'">
                  {{ item.judgmentType === JUDGMENT_TYPE_AUTO ? '自動判定' : '手動判定' }}
                </span>
                <span class="ws-item-name">{{ item.notificationName }}<span v-if="itemTooltip(item)" class="ws-item-help" :class="{ 'is-open': isTipOpen(item) }"><img class="ws-item-help__icon" src="@/assets/img/common/icon-help.svg" alt="" width="16" @click.stop="toggleItemTip(item)"><span v-if="isTipOpen(item)" class="ws-item-tip" @click.stop>{{ itemTooltip(item) }}</span></span><small v-if="item.supplement">{{ item.supplement }}</small></span>
              </div>
              <div
                class="ws-col ws-col--place"
                :class="{ 'is-alert': isAlert(wp, item) }"
                v-for="(wp, idx) in workplaces"
                :key="idx"
              >
                <div class="ws-need">
                  <button type="button" class="ws-need__btn ws-need__need" :class="{ 'is-active': cellOf(wp, item).necessity === NECESSITY_REQUIRED }" @click="setNecessity(wp, item, NECESSITY_REQUIRED)">必要</button>
                  <button type="button" class="ws-need__btn ws-need__not" :class="{ 'is-active': cellOf(wp, item).necessity === NECESSITY_UNNECESSARY }" @click="setNecessity(wp, item, NECESSITY_UNNECESSARY)">不要</button>
                </div>
                <div class="ws-status">
                  <template v-if="cellOf(wp, item).necessity === NECESSITY_REQUIRED">
                    <button v-if="cellOf(wp, item).status === STATUS_DONE" type="button" class="ws-status__done" @click="toggleStatus(wp, item)"><span class="ws-status__icon"></span>対応済</button>
                    <button v-else type="button" class="ws-status__pending" @click="toggleStatus(wp, item)"><span class="ws-status__icon ws-status__icon--pending"></span>未対応</button>
                  </template>
                  <span v-else class="ws-status__dash">—</span>
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- フッター保存 -->
        <div v-if="workplaces.length > 0" class="ws-footer-btn">
          <button type="button" class="ws-btn-save-lg" @click="save()">全データを保存</button>
        </div>

      </div>
    </div>
  </main>
</template>

<script>
import Loading from '@/components/parts/common/Loading.vue';
import CorporateSideMenu from '@/components/parts/corporate/CorporateSideMenu.vue';
import DiagCorpHeader from '@/components/parts/diag/DiagCorpHeader.vue';

// 従業員数区分ラベル(Workplace::EMP_TYPE_* に対応)
// ※ 仕様変更により「0名」(=1)は選択肢から廃止。値の採番は維持(2:1〜9名 〜 7:1001名以上)。
const EMP_TYPE_LABELS = {
  2: '1〜9名',
  3: '10〜49名',
  4: '50〜99名',
  5: '100〜299名',
  6: '300〜1000名',
  7: '1001名以上',
};

// 自動判定マトリクス: display_code ごとに、従業員数区分(employees_type 1〜7)での要否
//  値 true=必要 / false=不要。配列 index = employees_type(1:0名, 2:1〜9, 3:10〜49, 4:50〜99, 5:100〜299, 6:300〜1000, 7:1001名以上)
//  index 0 は未使用。0名(=1)はすべて不要。
const T = true, F = false;
const AUTO_NECESSITY = {
  10:  [null, F, F, T, T, T, T, T], // 就業規則の作成・届出
  20:  [null, F, T, T, T, T, T, T], // 36協定の締結・届出
  30:  [null, F, T, T, T, T, T, T], // 帳簿の備え付け
  120: [null, F, F, F, T, T, T, T], // 産業医の選任・届出
  130: [null, F, F, F, T, T, T, T], // ストレスチェックの実施・届出
  140: [null, F, F, F, T, T, T, T], // 定期健康診断結果の届出
  160: [null, F, F, T, F, F, F, F], // 衛生推進者の選任(10〜49名のみ必要)
  180: [null, F, F, F, T, T, T, T], // 衛生管理者の選任・届出
  200: [null, F, F, F, T, T, T, T], // 衛生委員会の実施
  220: [null, F, T, T, T, T, T, T], // 労働保険の成立
};

// 届出項目のツールチップ(DB管理せずHTMLに直接保持。display_code をキーに表示)
const NOTIFICATION_TOOLTIPS = {
  150: '「対象の業種」林業、鉱業、建設業、運送業、清掃業、製造業(物の加工業を含む)、電気業、ガス業、熱供給業、水道業、通信業、各種商品卸売業、家具・建具・じゅう器等卸売業、各種商品小売業、家具・建具・じゅう器小売業、燃料小売業、旅館業、ゴルフ場業、自動車整備業、機械修理業の場合必要です。',
  170: '「対象の業種」林業、鉱業、建設業、運送業、清掃業、製造業（物の加工業を含む。）、電気業、ガス業、熱供給業、水道業、通信業、各種商品卸売業、家具・建具・じゅう器等卸売業、各種商品小売業、家具・建具・じゅう器小売業、燃料小売業、旅館業、ゴルフ場業、自動車整備業、機械修理業の場合必要です。',
  190: '「対象の人数、業種」①常時50人以上で、林業、鉱業、建設業、製造業の一部の業種（木材・木製品製造業、化学工業、鉄鋼業、金属製品製造業、輸送用機械器具製造業）、運送業の一部の業種（道路貨物運送業、港湾運送業）、自動車整備業、機械修理業、清掃業、②常時100人以上で木材・木製品製造業、化学工業、鉄鋼業、金属製品製造業、輸送用機械器具製造業以外の製造業、道路貨物運送業、港湾運送業以外の運送業、電気業、ガス業、熱供給業、水道業、通信業、各種商品卸売業、家具・建具・じゅう器等卸売業、各種商品小売業、家具・建具・じゅう器小売業、燃料小売業、旅館業、ゴルフ場業の場合必要です。',
  210: '「対象となる人数、業種」①常時100人以上（林業、鉱業、建設業、運送業、清掃業）、②常時300人以上（製造業(物の加工業を含む。)、電気業、ガス業、熱供給業、水道業、通信業、各種商品卸売業、家具・建具・じゅう器等卸売業、各種商品小売業、家具・建具・じゅう器等小売業、燃料小売業、旅館業、ゴルフ場業、自動車整備業及び機械修理業）、③常時1000人以上（その他の業種）の場合必要です。',
};

const NECESSITY_REQUIRED = 1;
const NECESSITY_UNNECESSARY = 2;
const STATUS_DONE = 1;
const STATUS_NOT_YET = 2;
const JUDGMENT_TYPE_AUTO = 1;
const MAX_WORKPLACE_COUNT = 100;

export default {
  name: 'WorkplaceList',
  components: {
    Loading,
    DiagCorpHeader,
    CorporateSideMenu,
  },
  data() {
    return {
      isProcessing: false,
      isRendering: false,
      isAdmin: false,
      isSystemAdmin: false,
      isAllPermission: false,
      isDiagAvailablePlan: false,
      userAuthNo: '',
      dispCorpNm: '',
      corporateFeatureInfoLabels: [],
      workplaces: [],
      notificationItems: [],
      deletedWorkplaces: [],
      // 開いている届出項目ツールチップの display_code 一覧(複数同時表示可)
      openTips: [],
      // テンプレートから参照する定数
      NECESSITY_REQUIRED,
      NECESSITY_UNNECESSARY,
      STATUS_DONE,
      STATUS_NOT_YET,
      JUDGMENT_TYPE_AUTO,
    };
  },
  computed: {
    corpId() {
      return this.$store.getters['user/getCorpId'];
    },
    manageCorpName() {
      return this.$store.getters['auth/getUser'].manageCorpNm;
    },
    isAdminUser() {
      return this.userAuthNo == 3; // 専門家ユーザのみ
    },
    empTypeOptions() {
      return Object.keys(EMP_TYPE_LABELS).map((k) => ({ value: Number(k), label: EMP_TYPE_LABELS[k] }));
    },
    canAddWorkplace() {
      return this.workplaces.length < MAX_WORKPLACE_COUNT;
    },
  },
  created: async function() {
    this.isRendering = true;
    this.isProcessing = true;

    const authority = await this.$store.dispatch('auth/getAuthority');
    this.isSystemAdmin = authority.isSystemAdmin;
    this.isAllPermission = authority.isAllPermission;
    this.isAdmin = authority.isAdmin;
    this.userAuthNo = authority.userAuthNo;
    this.isDiagAvailablePlan = authority.isDiagAvailablePlan;

    await this.getCorporate(this.corpId);
    await this.getCorporateFeatureInfoLabels(this.corpId);
    await this.getWorkplaceData(this.corpId);

    this.isProcessing = false;
    this.$nextTick(() => {
      this.isRendering = false;
    });
  },
  mounted() {
    // 画面の他の場所をクリックしたらツールチップを閉じる(?アイコン・吹き出しのクリックは @click.stop で除外)
    document.addEventListener('click', this.closeAllTips);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeAllTips);
  },
  watch: {
    async corpId() {
      this.isRendering = true;
      this.isProcessing = true;
      await this.getCorporate(this.corpId);
      await this.getCorporateFeatureInfoLabels(this.corpId);
      await this.getWorkplaceData(this.corpId);
      this.$nextTick(() => {
        this.isRendering = false;
        this.isProcessing = false;
      });
    },
  },
  methods: {
    async getCorporate(corpId) {
      let res = await this.$axios.post('/ld/api/corporate/getCorporate', { corpId: corpId, isNew: false });
      if (res) {
        this.dispCorpNm = res.data.corporate.corpNm;
      }
    },
    async getCorporateFeatureInfoLabels(corpId) {
      let res = await this.$axios.post('/ld/api/corporate/getCorporateFeatureInfoLabels', { corpId: corpId });
      if (res) {
        this.corporateFeatureInfoLabels = res.data.corporateFeatureInfoLabels;
      }
    },
    async getWorkplaceData(corpId) {
      let res = await this.$axios.post('/ld/api/workplace/getWorkplaceData', { corpId: corpId });
      if (res) {
        this.notificationItems = res.data.notificationItems;
        const statuses = res.data.statuses;
        this.workplaces = res.data.workplaces.map((wp) => ({
          ...wp,
          _editing: false,
          statusMap: this.buildStatusMap(wp.workplaceId, statuses),
        }));
        this.deletedWorkplaces = [];
      }
    },
    // {itemId: {necessity, status}} を生成(全項目分のキーを先に作る。初期 necessity=null=未選択)
    buildStatusMap(workplaceId, statuses) {
      const map = {};
      this.notificationItems.forEach((item) => {
        map[item.workplaceNotificationItemId] = { necessity: null, status: null };
      });
      statuses
        .filter((s) => s.workplaceId === workplaceId)
        .forEach((s) => {
          map[s.workplaceNotificationItemId] = { necessity: s.necessity, status: s.status };
        });
      return map;
    },
    cellOf(wp, item) {
      return wp.statusMap[item.workplaceNotificationItemId];
    },
    isAlert(wp, item) {
      const cell = this.cellOf(wp, item);
      return cell.necessity === NECESSITY_REQUIRED && cell.status !== STATUS_DONE;
    },
    startEditName(wp, event) {
      wp._editing = true;
      // 入力欄にフォーカスを当てる(これにより外側クリックで blur→表示に戻る)
      this.$nextTick(() => {
        const container = event && event.target.closest('.ws-place-name');
        const input = container && container.querySelector('.ws-place-name-input');
        if (input) input.focus();
      });
    },
    addWorkplace() {
      if (!this.canAddWorkplace) return;
      const map = {};
      this.notificationItems.forEach((item) => {
        map[item.workplaceNotificationItemId] = { necessity: null, status: null };
      });
      const newWp = {
        workplaceId: null,
        workplaceName: '店舗・拠点 ' + (this.workplaces.length + 1),
        employeesType: null, // 既定: 未選択(「従業員数を選択」)
        remarks: '',
        _editing: true, // 追加直後は名前入力を活性化(blur/Enterで非活性)
        statusMap: map,
      };
      // 未選択状態で初期化(従業員数を選択するまで要否は判定しない)
      this.onEmployeesTypeChange(newWp);
      this.workplaces.push(newWp);
      // 追加した列が見えるよう一番右へスクロール
      this.$nextTick(() => {
        const el = this.$refs.scrollWrap;
        if (el) {
          el.scrollLeft = el.scrollWidth;
          // 追加した事業場(最右列)の名前入力にフォーカス
          const inputs = el.querySelectorAll('.ws-place-name-input');
          if (inputs.length) inputs[inputs.length - 1].focus();
        }
      });
    },
    removeWorkplace(idx) {
      const wp = this.workplaces[idx];
      if (!window.confirm('「' + (wp.workplaceName || '') + '」を削除しますか？')) return;
      if (wp.workplaceId) {
        this.deletedWorkplaces.push({ workplaceId: wp.workplaceId, deleteFlg: true });
      }
      this.workplaces.splice(idx, 1);
    },
    // 届出項目のツールチップ文言(無ければ空)
    itemTooltip(item) {
      return NOTIFICATION_TOOLTIPS[item.displayCode] || '';
    },
    isTipOpen(item) {
      return this.openTips.includes(item.displayCode);
    },
    // クリックでトグル。複数同時に開ける。?クリックは @click.stop で画面クリックの閉じる処理から除外
    toggleItemTip(item) {
      const i = this.openTips.indexOf(item.displayCode);
      if (i >= 0) this.openTips.splice(i, 1);
      else this.openTips.push(item.displayCode);
    },
    closeAllTips() {
      if (this.openTips.length) this.openTips = [];
    },
    autoNecessity(displayCode, employeesType) {
      if (employeesType == null) return null;
      const row = AUTO_NECESSITY[displayCode];
      if (row == null) return null;
      return row[employeesType] ? NECESSITY_REQUIRED : NECESSITY_UNNECESSARY;
    },
    // 従業員数区分変更時の再判定。
    //  自動判定項目: 区分から要否を判定。手動判定項目: 不要にリセット(モックアップ準拠)。
    //  必要になった項目は対応済を維持、それ以外は未対応。不要は対応状況null。
    onEmployeesTypeChange(wp) {
      // 「従業員数を選択」(null)に戻した場合は未選択状態へ戻す
      if (wp.employeesType == null) {
        this.notificationItems.forEach((item) => {
          const cell = wp.statusMap[item.workplaceNotificationItemId];
          cell.necessity = null;
          cell.status = null;
        });
        return;
      }
      this.notificationItems.forEach((item) => {
        const cell = wp.statusMap[item.workplaceNotificationItemId];
        let required = false;
        if (item.judgmentType === JUDGMENT_TYPE_AUTO) {
          required = this.autoNecessity(item.displayCode, wp.employeesType) === NECESSITY_REQUIRED;
        }
        if (required) {
          cell.necessity = NECESSITY_REQUIRED;
          if (cell.status !== STATUS_DONE) cell.status = STATUS_NOT_YET;
        } else {
          cell.necessity = NECESSITY_UNNECESSARY;
          cell.status = null;
        }
      });
    },
    setNecessity(wp, item, val) {
      const cell = this.cellOf(wp, item);
      if (cell.necessity === val) return;
      cell.necessity = val;
      if (val === NECESSITY_REQUIRED) {
        if (cell.status == null) cell.status = STATUS_NOT_YET;
      } else {
        cell.status = null;
      }
    },
    // 対応状況の単一トグル(対応済 ⟷ 未対応)
    toggleStatus(wp, item) {
      const cell = this.cellOf(wp, item);
      if (cell.necessity !== NECESSITY_REQUIRED) return;
      cell.status = cell.status === STATUS_DONE ? STATUS_NOT_YET : STATUS_DONE;
    },
    buildStatusesPayload(wp) {
      return this.notificationItems.map((item) => {
        const cell = wp.statusMap[item.workplaceNotificationItemId];
        return {
          workplaceNotificationItemId: item.workplaceNotificationItemId,
          necessity: cell.necessity,
          status: cell.status,
        };
      });
    },
    async save() {
      // 従業員数が未選択の事業場があればエラー(未選択の事業場名を表示)
      const unselectedNames = this.workplaces
        .filter((wp) => wp.employeesType == null)
        .map((wp) => `「${wp.workplaceName}」`);
      if (unselectedNames.length > 0) {
        this.$toast.error(`${unselectedNames.join('、')}の従業員数が未選択です。`);
        return;
      }
      this.isProcessing = true;
      const payload = {
        corpId: this.corpId,
        workplaces: [
          ...this.workplaces.map((wp) => ({
            workplaceId: wp.workplaceId,
            workplaceName: wp.workplaceName,
            employeesType: wp.employeesType,
            remarks: wp.remarks,
            deleteFlg: false,
            statuses: this.buildStatusesPayload(wp),
          })),
          ...this.deletedWorkplaces,
        ],
      };
      let res = await this.$axios.post('/ld/api/workplace/store', payload);
      if (res && res.status === 200) {
        this.$toast.info('保存しました。');
        await this.getWorkplaceData(this.corpId);
        // ヘッダーの事業場情報プレビューのキャッシュを無効化(次回ホバーで再取得)
        if (this.$refs.corpHeader) {
          this.$refs.corpHeader.invalidateWorkplacePreview();
        }
      } else {
        this.$toast.error((res && res.data && res.data.message) || '保存に失敗しました。');
      }
      this.isProcessing = false;
    },
    emitSelectCorpList(params) {
      this.$store.dispatch('user/setCorpId', params.corpId);
    },
  },
};
</script>

<style scoped>
/* 届出項目名の横のヘルプアイコン(? を基準に吹き出しを配置するため relative) */
.ws-item-help {
  position: relative;
  display: inline-block;
  margin-left: 4px;
  vertical-align: middle;
  line-height: 1;
  top: -2px; /* 項目名のテキストに対して少し上に揃える */
}
.ws-item-help__icon {
  cursor: pointer;
  vertical-align: middle;
}
/* ツールチップ吹き出し(? の子要素=? と一緒にスクロール。? の左端を基準に、余白のあるデータ列側(右)へ展開) */
.ws-item-tip {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 0;
  width: 480px;
  max-width: 70vw;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  font-size: 1.2rem;
  line-height: 1.7;
  text-align: left;
  padding: 10px 12px;
  border-radius: 4px;
  z-index: 100;
  white-space: normal;
  cursor: default;
}
/* 吹き出しの矢印(下向き=? を指す。? アイコン中央あたり) */
.ws-item-tip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 8px;
  border: 6px solid transparent;
  border-top-color: rgba(0, 0, 0, 0.85);
}
</style>
